import { useState, useEffect } from 'react'

import { Logger } from '../../../shared/utils/logger'

import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'

import {
  mockWindowData,
  mockProjectConfig,
  mockPanelSettings,
} from '../_mock/mockAppConfig'

/**
 * Resolves the WP-injected bootstrap data from window.bimCadaplusData.
 * In dev mode without a WP host, falls back to mock data.
 *
 * @returns {{ projectId: string, projectDataUrl: string, language: string, siteUrl: string }}
 */
function resolveWindowData() {
  if (window.bimCadaplusData) {
    return window.bimCadaplusData
  }

  if (import.meta.env.DEV) {
    Logger.warning({ message: 'window.bimCadaplusData not found — using dev mock' })
    return mockWindowData
  }

  return null
}

/**
 * Loads and merges project configuration at application startup.
 *
 * Performs three parallel REST fetches:
 *   1. GET /wp-json/bim-cadaplus/v1/projects/{id}/panel-config
 *      → full project entry from panel-config.json (hard config, build artifact)
 *   2. GET /wp-json/bim-cadaplus/v1/projects/{id}/panel-settings
 *      → panel-settings.json for the project (soft config, CMS-editable)
 *   3. GET /wp-json/bim-cadaplus/v1/projects/{id}/panel-styles
 *      → panel-styles.json for the project (design tokens, CMS-editable)
 *      Optional — missing file treated as {} (no overrides).
 *
 * In dev mode without window.bimCadaplusData, skips fetches and returns mock data.
 *
 * @returns {{
 *   projectConfig: object|null,
 *   panelSettings: object|null,
 *   panelStyles: object|null,
 *   windowData: object|null,
 *   configStatus: 'loading'|'ready'|'error',
 *   configError: string|null,
 * }}
 */
export function useAppConfig() {
  // Lazy initializer — resolveWindowData() is synchronous (reads window.* or returns mock),
  // so windowData is available on the very first render. This allows useProjectLogo to start
  // HEAD requests immediately without waiting for the config fetch useEffect to fire.
  const [state, setState] = useState(() => {
    const windowData = resolveWindowData()
    return {
      projectConfig: null,
      panelSettings: null,
      panelStyles: null,
      windowData,
      configStatus: FETCH_STATUS.LOADING,
      configError: null,
    }
  })
  useEffect(() => {
    let cancelled = false

    async function loadConfig() {
      const windowData = resolveWindowData()

      if (!windowData) {
        setState((s) => ({
          ...s,
          configStatus: FETCH_STATUS.ERROR,
          configError: 'window.bimCadaplusData is not defined',
        }))
        return
      }

      // Dev mock path — skip real REST fetches, load from .dev_data/ if available
      if (import.meta.env.DEV && windowData === mockWindowData) {
        let panelSettings = mockPanelSettings
        try {
          const res = await fetch('/.dev_data/panel-settings.json')
          if (res.ok) {
            panelSettings = await res.json()
            Logger.info({ message: 'panel-settings loaded from .dev_data/', content: windowData.projectId })
          }
        } catch {
          // file absent or malformed — fall back to inline mock silently
        }

        let panelStyles = null
        try {
          const res = await fetch('/.dev_data/panel-styles.json')
          if (res.ok) {
            panelStyles = await res.json()
            Logger.info({ message: 'panel-styles loaded from .dev_data/', content: windowData.projectId })
          }
        } catch {
          // optional file — absence is not an error
        }

        Logger.info({ message: 'Config loaded from dev mock', content: windowData.projectId })
        setState({
          projectConfig: mockProjectConfig,
          panelSettings,
          panelStyles,
          windowData,
          configStatus: FETCH_STATUS.READY,
          configError: null,
        })
        return
      }

      const { projectId, siteUrl } = windowData
      const base = `${siteUrl}/wp-json/bim-cadaplus/v1`
      const timestamp = new Date().getTime(); //Cache-busting for proxy servers

      try {
        const [configRes, settingsRes, stylesRes] = await Promise.all([
          fetch(`${base}/projects/${projectId}/panel-config?_cb=${timestamp}`),
          fetch(`${base}/projects/${projectId}/panel-settings?_cb=${timestamp}`),
          fetch(`${base}/projects/${projectId}/panel-styles?_cb=${timestamp}`),
        ])

        if (!configRes.ok) {
          const body = await configRes.json().catch(() => ({}))
          throw new Error(body.message ?? `panel-config fetch failed (${configRes.status})`)
        }

        const projectConfig = await configRes.json()

        // panel-settings is optional — missing file returns {} with 200, 404 means project not found
        let panelSettings = {}
        if (settingsRes.ok) {
          panelSettings = await settingsRes.json()
        } else if (settingsRes.status !== 404) {
          Logger.warning({
            message: 'panel-settings fetch returned unexpected status',
            content: String(settingsRes.status),
          })
        }

        // panel-styles is optional — absence means no design token overrides
        let panelStyles = null
        if (stylesRes.ok) {
          panelStyles = await stylesRes.json()
        } else if (stylesRes.status !== 404) {
          Logger.warning({
            message: 'panel-styles fetch returned unexpected status',
            content: String(stylesRes.status),
          })
        }

        if (cancelled) return

        Logger.success({ message: 'Config loaded', content: projectId })

        setState({
          projectConfig,
          panelSettings,
          panelStyles,
          windowData,
          configStatus: FETCH_STATUS.READY,
          configError: null,
        })
      } catch (err) {
        if (cancelled) return

        Logger.error({ message: 'Config load failed', content: err.message })

        setState((s) => ({
          ...s,
          windowData,
          configStatus: FETCH_STATUS.ERROR,
          configError: err.message,
        }))
      }
    }

    loadConfig()

    return () => {
      cancelled = true
    }
  }, [])

  return state
}
